import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  Skeleton,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Stars as PointsIcon,
  LocalFireDepartment as StreakIcon,
  EmojiEvents as BadgeIcon,
  WorkspacePremium as CertIcon,
  Download as DownloadIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
  School as CourseIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { userApi, certificateApi, Badge } from '../services/api';

function ProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => userApi.getProfile().then((r) => r.data),
  });

  const { data: progress } = useQuery({
    queryKey: ['progress-summary'],
    queryFn: () => userApi.getProgressSummary().then((r) => r.data),
  });

  // Fetch user badges
  const { data: badges = [] } = useQuery({
    queryKey: ['my-badges'],
    queryFn: () => userApi.getBadges().then((r) => r.data),
  });

  // Fetch user certificates
  const { data: certificates = [] } = useQuery({
    queryKey: ['my-certificates'],
    queryFn: () => userApi.getCertificates().then((r) => r.data),
  });

  // Handle certificate download
  const handleDownloadCertificate = async (certId: string, certName: string) => {
    try {
      const response = await certificateApi.download(certId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Zertifikat_${certName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download certificate:', error);
    }
  };

  const earnedBadges = badges.filter((b: Badge) => b.earned);
  const earnedCertificates = Array.isArray(certificates) ? certificates : [];

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Mein Profil</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
          </Grid>
          <Grid item xs={12} md={8}>
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom>Mein Profil</Typography>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                src={profile?.avatarUrl}
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: 48,
                }}
              >
                {profile?.displayName?.charAt(0) || user?.name?.charAt(0) || 'U'}
              </Avatar>
              <Typography variant="h5" gutterBottom>
                {profile?.displayName || user?.name || 'Benutzer'}
              </Typography>
              <Chip
                label={profile?.currentSkillLevel?.name || 'Einsteiger'}
                color="primary"
                sx={{ mb: 2 }}
              />
              <Divider sx={{ my: 2 }} />
              <List dense>
                <ListItem>
                  <ListItemIcon><PersonIcon /></ListItemIcon>
                  <ListItemText primary={user?.username} secondary="Benutzername" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><EmailIcon /></ListItemIcon>
                  <ListItemText primary={user?.email} secondary="E-Mail" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Stats Card */}
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
              <Typography variant="h6" gutterBottom>Statistiken</Typography>
              {isMobile ? (
                // Mobile: Horizontal scroll
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    overflowX: 'auto',
                    pb: 1,
                    mx: -1.5,
                    px: 1.5,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                  }}
                >
                  {[
                    { icon: <PointsIcon sx={{ fontSize: 32, color: 'primary.main' }} />, value: progress?.totalPoints || 0, label: 'Punkte', onClick: undefined },
                    { icon: <StreakIcon sx={{ fontSize: 32, color: 'warning.main' }} />, value: progress?.currentStreak || 0, label: 'Streak', onClick: undefined },
                    { icon: <BadgeIcon sx={{ fontSize: 32, color: 'success.main' }} />, value: earnedBadges.length, label: 'Badges', onClick: () => navigate('/badges') },
                    { icon: <CertIcon sx={{ fontSize: 32, color: 'info.main' }} />, value: earnedCertificates.length, label: 'Zertifikate', onClick: () => navigate('/certificates') },
                  ].map((stat, idx) => (
                    <Paper
                      key={idx}
                      onClick={stat.onClick}
                      sx={{
                        p: 2,
                        minWidth: 100,
                        textAlign: 'center',
                        flexShrink: 0,
                        cursor: stat.onClick ? 'pointer' : 'default',
                        '&:active': stat.onClick ? { transform: 'scale(0.98)' } : {},
                      }}
                    >
                      {stat.icon}
                      <Typography variant="h5" fontWeight="bold">{stat.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                // Desktop: Grid
                <Grid container spacing={3}>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <PointsIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h4">{progress?.totalPoints || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Punkte</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <StreakIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                      <Typography variant="h4">{progress?.currentStreak || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">Tage Streak</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center" sx={{ cursor: 'pointer' }} onClick={() => navigate('/badges')}>
                      <BadgeIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                      <Typography variant="h4">{earnedBadges.length}</Typography>
                      <Typography variant="body2" color="text.secondary">Badges</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center" sx={{ cursor: 'pointer' }} onClick={() => navigate('/certificates')}>
                      <CertIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                      <Typography variant="h4">{earnedCertificates.length}</Typography>
                      <Typography variant="body2" color="text.secondary">Zertifikate</Typography>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Level-Fortschritt</Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="body1">
                  Level {progress?.level}: {progress?.levelName}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {progress?.totalPoints || 0} Punkte gesammelt
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Badges Gallery */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  <BadgeIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Meine Abzeichen
                </Typography>
                <Button
                  endIcon={<ArrowIcon />}
                  onClick={() => navigate('/badges')}
                  size="small"
                >
                  Alle anzeigen
                </Button>
              </Box>

              {earnedBadges.length > 0 ? (
                <Box display="flex" gap={2} flexWrap="wrap">
                  {earnedBadges.slice(0, 6).map((badge: Badge) => (
                    <Tooltip key={badge.id} title={badge.description}>
                      <Paper
                        elevation={2}
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          minWidth: 100,
                          bgcolor: 'background.default',
                        }}
                      >
                        {badge.imageUrl ? (
                          <Avatar
                            src={badge.imageUrl}
                            sx={{ width: 56, height: 56, mx: 'auto', mb: 1 }}
                          />
                        ) : (
                          <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 1, bgcolor: 'success.main' }}>
                            <BadgeIcon />
                          </Avatar>
                        )}
                        <Typography variant="body2" noWrap sx={{ maxWidth: 80 }}>
                          {badge.name}
                        </Typography>
                      </Paper>
                    </Tooltip>
                  ))}
                </Box>
              ) : (
                <Box textAlign="center" py={3} color="text.secondary">
                  <BadgeIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography>Noch keine Abzeichen verdient</Typography>
                  <Button
                    variant="outlined"
                    sx={{ mt: 2 }}
                    onClick={() => navigate('/courses')}
                  >
                    Kurse durchsuchen
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Certificates List */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  <CertIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  Meine Zertifikate
                </Typography>
                <Button
                  endIcon={<ArrowIcon />}
                  onClick={() => navigate('/certificates')}
                  size="small"
                >
                  Alle anzeigen
                </Button>
              </Box>

              {earnedCertificates.length > 0 ? (
                <List>
                  {earnedCertificates.slice(0, 3).map((cert: any) => (
                    <ListItem
                      key={cert.id}
                      secondaryAction={
                        <Tooltip title="Zertifikat herunterladen">
                          <IconButton
                            edge="end"
                            onClick={() => handleDownloadCertificate(cert.id, cert.certificate?.name || 'Zertifikat')}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'info.main' }}>
                          <CertIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={cert.certificate?.name || 'Zertifikat'}
                        secondary={
                          <>
                            {cert.certificate?.course?.title && (
                              <Typography component="span" variant="body2" color="text.secondary">
                                {cert.certificate.course.title}
                              </Typography>
                            )}
                            {cert.issuedAt && (
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                - Ausgestellt am {new Date(cert.issuedAt).toLocaleDateString('de-DE')}
                              </Typography>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box textAlign="center" py={3} color="text.secondary">
                  <CertIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
                  <Typography>Noch keine Zertifikate erhalten</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Schliessen Sie Kurse ab, um Zertifikate zu erhalten.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ProfilePage;
